import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { LoaderService } from '@app/core/services/loader.service';
import { SessionService } from '@app/core/services/session.service';
import { ToastrService } from '@app/shared/services/toastr.service';
import { MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { ToastModule } from 'primeng/toast';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { FooterComponent } from './core/components/footer/footer.component';
import { NavMenuComponent } from './core/components/nav-menu/nav-menu.component';
import { ProgressBarModule } from 'primeng/progressbar';
import { ActiveMessagesComponent } from './messages/active-messages/active-messages.component';

@Component({
  selector: 'vp-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  providers: [MessageService],
  imports: [
    CommonModule,
    RouterOutlet,
    ToastModule,
    NavMenuComponent,
    FooterComponent,
    ProgressBarModule,
    ActiveMessagesComponent
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  constructor(
    private toastrService: ToastrService,
    private messageService: MessageService,
    public router: Router,
    public loaderService: LoaderService,
    private primengConfig: PrimeNG,
    private sessionService: SessionService,
  ) {
    this.listenOnRouteChanges();
  }

  ngOnInit() {
    this.primengConfig.ripple.set(true);
    this.listenOnMessageChanges();
    this.listenOnUserActions();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  listenOnRouteChanges() {
    this.router.events.subscribe((event: Event) => {
      switch (true) {
        case event instanceof NavigationStart:
          Promise.resolve().then(() => this.loaderService.show());
          break;
        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError:
          this.loaderService.hide();
          break;
        default:
          break;
      }
    });
  }

  listenOnMessageChanges() {
    this.toastrService.messages$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(messsage => this.messageService.add(messsage));
  }

  listenOnUserActions() {
    merge(
      ...['mousedown', 'mousemove', 'keydown', 'focusin'].map(action => fromEvent(document, action)),
      ...['resize', 'scroll'].map(action => fromEvent(window, action, { capture: true }))
    )
      .pipe(
        debounceTime(500),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => this.sessionService.setSessionExpire());
  }
}
